import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AnswersLeftChip from "@/components/AnswersLeftChip";

/**
 * Slim top header — shown on public/marketing routes (Landing / Login /
 * Register / ForgotPassword / ResetPassword / BirthForm).
 *
 * On the Landing page (path === "/") the header adopts a LIGHT palette
 * (amura-inspired white surface). On every other public route it stays
 * dark cosmic. Authenticated app routes render AppShell instead.
 */
export default function Header() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // Suppress on shell-driven pages AND on the pages that ship their own custom nav (Phase V royal redesign).
    if (
        pathname === "/" ||
        pathname === "/start" ||
        pathname === "/login" ||
        pathname === "/register" ||
        pathname === "/forgot-password" ||
        pathname.startsWith("/reset-password") ||
        pathname === "/account" ||
        pathname.startsWith("/roadmap/") ||
        pathname === "/pricing" ||
        pathname === "/admin" ||
        pathname === "/horary" ||
        pathname === "/synastry" ||
        pathname.startsWith("/deep-dive/")
    ) {
        return null;
    }

    const light = false;

    // Style tokens — swap palette but keep the same structure.
    const shellCls = light
        ? "sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-stone-200/80"
        : "sticky top-0 z-40 bg-cosmos-void/80 backdrop-blur-xl border-b border-white/[0.06]";

    const brandTextCls = light ? "text-stone-900" : "text-white";
    const brandSubCls = light ? "text-aurora-700/85" : "text-gold/80";
    const ringInner = light ? "border-stone-300" : "border-white/15";
    const navLinkCls = light
        ? "text-stone-700 hover:text-aurora-700 transition-colors"
        : "text-white/55 hover:text-white transition-colors";
    const signInCls = light
        ? "text-sm text-stone-700 hidden sm:block hover:text-aurora-700 transition-colors"
        : "text-sm text-white/80 hidden sm:block hover:text-white transition-colors";
    const ctaCls = light
        ? "inline-block text-[13px] px-4 py-2 rounded-full bg-cosmos-ink text-white hover:bg-aurora-800 transition-colors"
        : "btn-gold inline-block text-[13px] px-4 py-2";
    const signOutCls = light
        ? "text-xs text-stone-500 hover:text-aurora-700 underline underline-offset-4"
        : "text-xs text-white/45 hover:text-white underline underline-offset-4";
    const usernameCls = light ? "text-stone-800 truncate max-w-[120px]" : "text-white/90 truncate max-w-[120px]";

    return (
        <header className={shellCls}>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid={TID.nav.logo}
                    className="flex items-center gap-3 group"
                >
                    <div className="relative w-8 h-8 grid place-items-center">
                        <div className="absolute inset-0 rounded-full border border-gold/60 group-hover:rotate-180 transition-transform duration-700" />
                        <div className={`absolute inset-1.5 rounded-full border ${ringInner}`} />
                        <div className="w-1 h-1 bg-gold rounded-full shadow-[0_0_8px_rgba(212,175,55,0.8)]" />
                    </div>
                    <div className="leading-tight">
                        <div className={`font-display text-[19px] tracking-tight ${brandTextCls}`}>
                            Lodestar
                        </div>
                        <div className={`text-[9px] font-mono tracking-[0.25em] uppercase ${brandSubCls}`}>
                            Cosmic life mapping
                        </div>
                    </div>
                </Link>

                <nav className={`hidden md:flex items-center gap-7 text-[13px] ${light ? "" : "text-white/55"}`}>
                    <a href="/#promises" className={navLinkCls}>What you get</a>
                    <Link to="/pricing" className={navLinkCls}>Pricing</Link>
                    {user && (
                        <Link to="/account" data-testid={TID.auth.accountLink} className={navLinkCls}>
                            My account
                        </Link>
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    <AnswersLeftChip variant={light ? "light" : "dark"} compact />
                    <LanguageSwitcher variant={light ? "light" : "dark"} testId="header-lang-switcher" />
                    {user ? (
                        <div className="flex items-center gap-3" data-testid={TID.auth.userMenu}>
                            <Link to="/account" className="hidden sm:flex items-center gap-2 text-sm">
                                {user.picture ? (
                                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-gold/40" />
                                ) : (
                                    <div className="w-7 h-7 bg-gradient-to-br from-gold to-gold-deep text-cosmos-void grid place-items-center font-mono text-[11px] font-semibold rounded-full">
                                        {(user.name || user.email)[0]?.toUpperCase()}
                                    </div>
                                )}
                                <span className={usernameCls}>
                                    {user.name || user.email.split("@")[0]}
                                </span>
                            </Link>
                            <button
                                type="button"
                                onClick={async () => {
                                    await logout();
                                    navigate("/");
                                }}
                                data-testid={TID.auth.logout}
                                className={signOutCls}
                            >
                                Sign out
                            </button>
                        </div>
                    ) : (
                        <>
                            <Link to="/login" className={signInCls}>Sign in</Link>
                            <Link
                                to="/start"
                                data-testid={TID.nav.getStarted}
                                className={ctaCls}
                            >
                                Get started →
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
